import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4011';

export interface PlatformStats {
  users: {
    total: number;
    active: number;
    banned: number;
    premium: number;
    banRate: string;
  };
  sessions: {
    total: number;
    today: number;
    thisWeek: number;
  };
  reports: {
    totalReports: number;
    unreviewedReports: number;
    reportsLast24h: number;
  };
  adminActions: {
    total: number;
    today: number;
    autoBans: number;
    manualBans: number;
  };
}

export interface UserDetails {
  user: {
    id: string;
    email: string;
    isEmailVerified: boolean;
    isPremium: boolean;
    banned: boolean;
    banReason: string | null;
    banExpiresAt: string | null;
    createdAt: string;
    lastLoginAt: string | null;
    stats: any;
  };
  riskAssessment: any;
  recentSessions: any[];
  reportsAgainst: any[];
}

export interface AuditLogEntry {
  id: string;
  adminId: string;
  targetUserId: string;
  action: string;
  reason: string | null;
  metadata: any;
  createdAt: string;
  admin: { id: string; email: string } | null;
  targetUser: { id: string; email: string };
}

export interface ReportWithDetails {
  id: string;
  reporterId: string;
  reportedId: string;
  reason: string;
  reviewed: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  action: string | null;
  createdAt: string;
  reporter: {
    id: string;
    email: string;
    createdAt: string;
    banned: boolean;
  };
  reported: {
    id: string;
    email: string;
    createdAt: string;
    banned: boolean;
    stats: {
      totalReportsReceived: number;
      totalSessions: number;
    };
  };
}

export class AdminService {
  /**
   * Get platform statistics
   */
  static async getPlatformStats(accessToken: string): Promise<PlatformStats> {
    const response = await axios.get(`${API_URL}/admin/stats`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  }

  /**
   * Get all reports (paginated)
   */
  static async getReports(
    accessToken: string,
    page: number = 1,
    limit: number = 20,
    reviewed?: boolean,
  ): Promise<{ reports: ReportWithDetails[]; pagination: any }> {
    const params: any = { page, limit };
    if (reviewed !== undefined) {
      params.reviewed = reviewed;
    }

    const response = await axios.get(`${API_URL}/admin/reports`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      params,
    });
    return response.data;
  }

  /**
   * Get user details
   */
  static async getUserDetails(
    accessToken: string,
    userId: string,
  ): Promise<UserDetails> {
    const response = await axios.get(`${API_URL}/admin/users/${userId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  }

  /**
   * Ban a user
   */
  static async banUser(
    accessToken: string,
    userId: string,
    reason: string,
    durationDays?: number,
  ): Promise<any> {
    const response = await axios.patch(
      `${API_URL}/admin/users/${userId}/ban`,
      { reason, durationDays },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  }

  /**
   * Unban a user
   */
  static async unbanUser(accessToken: string, userId: string): Promise<any> {
    const response = await axios.patch(
      `${API_URL}/admin/users/${userId}/unban`,
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  }

  /**
   * Review a report
   */
  static async reviewReport(
    accessToken: string,
    reportId: string,
    action: 'DISMISSED' | 'WARNED' | 'BANNED',
  ): Promise<any> {
    const response = await axios.patch(
      `${API_URL}/admin/reports/${reportId}/review`,
      { action },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  }

  /**
   * Get audit log
   */
  static async getAuditLog(
    accessToken: string,
    page: number = 1,
    limit: number = 50,
  ): Promise<{ actions: AuditLogEntry[]; pagination: any }> {
    const response = await axios.get(`${API_URL}/admin/audit-log`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: { page, limit },
    });
    return response.data;
  }
}

export default AdminService;
